// Stok sayımı Excel: sayım şablonu (doldurulup geri alınır), sonuç/fark dökümü ve
// Excel'den sayım alma. Eşleştirme kuralları saf fonksiyondur (matchImportRows), test edilir.
import { parseTrQuantity } from '../../../../utils/numberFormat';
import type {
  StockCountDetail, StockCountDifferenceRow, StockCountLine, StockCountReview,
} from './stockCountService';

const loadExcel = async () => {
  const [ExcelJS, { saveAs }] = await Promise.all([import('exceljs'), import('file-saver')]);
  return { ExcelJS: (ExcelJS as any).default ?? ExcelJS, saveAs };
};

const safeName = (s: string) => s.replace(/[\\/:*?"<>|]+/g, '-');

const styleHeader = (row: any) => {
  row.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1257B8' } };
  row.alignment = { vertical: 'middle' };
};

async function download(wb: any, fileName: string) {
  const { saveAs } = await loadExcel();
  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), safeName(fileName));
}

/** Doldurulacak sayım şablonu: sayım satırları + boş "Sayılan" sütunu. Gizli "SatırId" eşleştirmeyi sağlar. */
export async function exportCountTemplate(count: StockCountDetail) {
  const { ExcelJS } = await loadExcel();
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Sayım');
  const cols = [
    { header: 'Sıra', key: 'no', width: 6 },
    { header: 'Ürün', key: 'name', width: 40 },
    { header: 'Stok Kodu', key: 'code', width: 16 },
    { header: 'Barkod', key: 'barcode', width: 18 },
    { header: 'Lot', key: 'lot', width: 14 },
    { header: 'Birim', key: 'unit', width: 10 },
    ...(count.showsExpected ? [{ header: 'Sistemde', key: 'expected', width: 12 }] : []),
    { header: 'Sayılan', key: 'counted', width: 12 },
    { header: 'SatırId', key: 'lineId', width: 38, hidden: true },
  ];
  ws.columns = cols;
  styleHeader(ws.getRow(1));
  count.lines.forEach((l, i) => {
    ws.addRow({
      no: i + 1,
      name: l.itemName ?? '',
      code: l.stockCode ?? '',
      barcode: l.barcode ?? '',
      lot: l.lotNo ?? '',
      unit: l.unit ?? '',
      expected: l.snapshotQty ?? null,
      counted: l.countedQty ?? null,
      lineId: l.id,
    });
  });
  ws.getColumn('counted').eachCell({ includeEmpty: true }, (cell: any, rowNo: number) => {
    if (rowNo > 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF8E1' } };
  });
  ws.views = [{ state: 'frozen', ySplit: 1 }];
  await download(wb, `Sayim-${count.number}.xlsx`);
}

/** İnceleme / sonuç dökümü: tüm satırlar ve farklar. */
export async function exportReviewExcel(count: StockCountDetail, review: StockCountReview) {
  const { ExcelJS } = await loadExcel();
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Sayım sonucu');
  ws.columns = [
    { header: 'Ürün', key: 'name', width: 40 },
    { header: 'Stok Kodu', key: 'code', width: 16 },
    { header: 'Lot', key: 'lot', width: 14 },
    { header: 'Birim', key: 'unit', width: 10 },
    { header: 'Başlangıçta', key: 'snap', width: 12 },
    { header: 'Sayım sırasında', key: 'move', width: 14 },
    { header: 'Beklenen', key: 'exp', width: 12 },
    { header: 'Sayılan', key: 'cnt', width: 12 },
    { header: 'Fark', key: 'diff', width: 12 },
    { header: 'Birim maliyet', key: 'cost', width: 14 },
    { header: 'Fark tutarı', key: 'val', width: 14 },
    { header: 'Sayan', key: 'by', width: 24 },
    { header: 'Not', key: 'note', width: 28 },
  ];
  styleHeader(ws.getRow(1));
  for (const l of review.lines) {
    const notes = [
      l.overTolerance && 'Tolerans üstü',
      l.isOutOfScope && 'Kapsam dışı',
      l.hasManualEntry && 'Elle girildi',
      l.movedDuringCount && 'Sayımda hareket',
    ].filter(Boolean).join(', ');
    ws.addRow({
      name: l.itemName ?? '', code: l.stockCode ?? '', lot: l.lotNo ?? '', unit: l.unit ?? '',
      snap: l.snapshotQty, move: l.movementQty, exp: l.expectedQty,
      cnt: l.countedQty ?? 'sayılmadı', diff: l.differenceQty ?? null,
      cost: l.unitCost, val: l.differenceValue ?? null, by: l.countedBy.join(', '), note: notes,
    });
  }
  ['cost', 'val'].forEach((k) => { ws.getColumn(k).numFmt = '#,##0.00 ₺'; });
  ws.addRow({});
  ws.addRow({ name: 'Sayım fazlası', val: review.surplusValue }).font = { bold: true };
  ws.addRow({ name: 'Sayım eksiği', val: review.shortageValue }).font = { bold: true };
  ws.addRow({ name: 'Net fark', val: review.surplusValue + review.shortageValue }).font = { bold: true };
  if (review.unknownBarcodes.length) {
    const u = wb.addWorksheet('Tanınmayan barkodlar');
    u.columns = [{ header: 'Barkod', key: 'b', width: 22 }, { header: 'Miktar', key: 'q', width: 10 }, { header: 'Sayan', key: 'by', width: 24 }];
    styleHeader(u.getRow(1));
    review.unknownBarcodes.forEach((x) => u.addRow({ b: x.barcode, q: x.quantity, by: x.countedBy.join(', ') }));
  }
  ws.views = [{ state: 'frozen', ySplit: 1 }];
  await download(wb, `Sayim-sonucu-${count.number}.xlsx`);
}

/** Sayım farkları raporu dökümü. */
export async function exportDifferencesExcel(rows: StockCountDifferenceRow[], fileLabel: string) {
  const { ExcelJS } = await loadExcel();
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Sayım farkları');
  ws.columns = [
    { header: 'Onay tarihi', key: 'date', width: 18 },
    { header: 'Sayım', key: 'no', width: 16 },
    { header: 'Sayım adı', key: 'cname', width: 22 },
    { header: 'Depo', key: 'wh', width: 22 },
    { header: 'Ürün', key: 'name', width: 36 },
    { header: 'Stok Kodu', key: 'code', width: 14 },
    { header: 'Lot', key: 'lot', width: 12 },
    { header: 'Birim', key: 'unit', width: 8 },
    { header: 'Beklenen', key: 'exp', width: 11 },
    { header: 'Sayılan', key: 'cnt', width: 11 },
    { header: 'Fark', key: 'diff', width: 11 },
    { header: 'Birim maliyet', key: 'cost', width: 13 },
    { header: 'Fark tutarı', key: 'val', width: 13 },
  ];
  styleHeader(ws.getRow(1));
  for (const r of rows) {
    ws.addRow({
      date: r.approvedAt ? new Date(r.approvedAt.endsWith('Z') ? r.approvedAt : `${r.approvedAt}Z`) : null,
      no: r.number, cname: r.countName ?? '', wh: r.warehouseName ?? '', name: r.itemName ?? '',
      code: r.stockCode ?? '', lot: r.lotNo ?? '', unit: r.unit ?? '',
      exp: r.expectedQty ?? null, cnt: r.countedQty ?? null, diff: r.differenceQty ?? null,
      cost: r.unitCost, val: r.differenceValue ?? null,
    });
  }
  ws.getColumn('date').numFmt = 'dd.mm.yyyy hh:mm';
  ['cost', 'val'].forEach((k) => { ws.getColumn(k).numFmt = '#,##0.00 ₺'; });
  ws.views = [{ state: 'frozen', ySplit: 1 }];
  await download(wb, `Sayim-farklari-${fileLabel}.xlsx`);
}

// ───────────────────────── Excel'den sayım alma ─────────────────────────

export interface ImportRow {
  rowNo: number;
  lineId?: string;
  barcode?: string;
  stockCode?: string;
  name?: string;
  lot?: string;
  /** NaN: okunamadı. */
  qty: number;
  /** Miktar hücresi boş (sayılmadı). */
  empty?: boolean;
}

export interface ImportMatch {
  line: StockCountLine;
  qty: number;
  rows: number[];
}

export interface ImportPlan {
  matched: ImportMatch[];
  /** Listede yok ama barkodu var: barkodla eklenir (kapsam dışı ya da tanınmayan olarak). */
  byBarcode: ImportRow[];
  invalid: { rowNo: number; reason: string }[];
  /** Miktar hücresi boş satırlar (sayılmadı sayılır, dokunulmaz). */
  skipped: number;
}

const norm = (v: unknown) => String(v ?? '').trim();
const key = (v: unknown) => norm(v).toLocaleUpperCase('tr-TR');

const HEADER_ALIASES: Record<string, string[]> = {
  lineId: ['SATIRID', 'SATIR ID'],
  barcode: ['BARKOD', 'BARCODE'],
  stockCode: ['STOK KODU', 'STOKKODU', 'KOD', 'STOCK CODE'],
  name: ['ÜRÜN', 'URUN', 'ÜRÜN ADI', 'PRODUCT'],
  lot: ['LOT', 'LOT NO', 'PARTİ'],
  qty: ['SAYILAN', 'MİKTAR', 'MIKTAR', 'SAYIM', 'COUNTED', 'QUANTITY'],
};

const cellText = (v: any): string => {
  if (v == null) return '';
  if (typeof v === 'object') {
    if ('result' in v) return cellText(v.result);
    if ('text' in v) return String(v.text);
    if (Array.isArray(v.richText)) return v.richText.map((r: any) => r.text).join('');
  }
  return String(v);
};

const toQty = (v: any): number | null => {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  const t = cellText(v).trim();
  if (!t) return null;
  return parseTrQuantity(t, 3);
};

/** Dosyayı okur; başlık satırı ilk 10 satırda aranır. "Sayılan/Miktar" ve "Barkod/Stok Kodu/SatırId"den biri gerekir. */
export async function readImportFile(file: File): Promise<ImportRow[]> {
  const { ExcelJS } = await loadExcel();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const ws = wb.worksheets[0];
  if (!ws) throw new Error('Dosyada sayfa yok.');

  let headerRow = 0;
  const colOf: Record<string, number> = {};
  for (let r = 1; r <= Math.min(10, ws.rowCount) && !headerRow; r += 1) {
    const row = ws.getRow(r);
    const found: Record<string, number> = {};
    row.eachCell((cell: any, col: number) => {
      const h = key(cellText(cell.value));
      for (const [k, aliases] of Object.entries(HEADER_ALIASES)) {
        if (!(k in found) && aliases.includes(h)) found[k] = col;
      }
    });
    if ('qty' in found && ('barcode' in found || 'stockCode' in found || 'lineId' in found)) {
      headerRow = r;
      Object.assign(colOf, found);
    }
  }
  if (!headerRow) {
    throw new Error('Başlık satırı bulunamadı. "Sayılan" (ya da "Miktar") ve "Barkod" ya da "Stok Kodu" sütunları olmalı.');
  }

  const out: ImportRow[] = [];
  for (let r = headerRow + 1; r <= ws.rowCount; r += 1) {
    const row = ws.getRow(r);
    const get = (k: string) => (colOf[k] ? row.getCell(colOf[k]).value : null);
    const lineId = norm(cellText(get('lineId')));
    const barcode = norm(cellText(get('barcode')));
    const stockCode = norm(cellText(get('stockCode')));
    const name = norm(cellText(get('name')));
    if (!lineId && !barcode && !stockCode && !name) continue;
    const q = toQty(get('qty'));
    out.push({
      rowNo: r,
      lineId: lineId || undefined,
      barcode: barcode || undefined,
      stockCode: stockCode || undefined,
      name: name || undefined,
      lot: norm(cellText(get('lot'))) || undefined,
      qty: q == null ? NaN : q,
      empty: q == null,
    });
  }
  return out;
}

/**
 * Satırları sayım satırlarıyla eşleştirir. Öncelik: SatırId, sonra barkod, sonra stok kodu.
 * Aynı ürünün birden çok lot satırı varsa Lot sütunu gerekir. Aynı satıra düşen miktarlar toplanır.
 */
export function matchImportRows(rows: ImportRow[], lines: StockCountLine[]): ImportPlan {
  const byId = new Map(lines.map((l) => [l.id, l]));
  const byBarcode = new Map<string, StockCountLine[]>();
  const byCode = new Map<string, StockCountLine[]>();
  for (const l of lines) {
    if (l.barcode) {
      const k = key(l.barcode);
      byBarcode.set(k, [...(byBarcode.get(k) ?? []), l]);
    }
    if (l.stockCode) {
      const k = key(l.stockCode);
      byCode.set(k, [...(byCode.get(k) ?? []), l]);
    }
  }

  const plan: ImportPlan = { matched: [], byBarcode: [], invalid: [], skipped: 0 };
  const acc = new Map<string, ImportMatch>();

  for (const r of rows) {
    if (r.empty) { plan.skipped += 1; continue; }
    if (!Number.isFinite(r.qty) || r.qty < 0) {
      plan.invalid.push({ rowNo: r.rowNo, reason: 'Miktar okunamadı' });
      continue;
    }
    let candidates: StockCountLine[] = [];
    if (r.lineId && byId.has(r.lineId)) candidates = [byId.get(r.lineId)!];
    else if (r.barcode && byBarcode.has(key(r.barcode))) candidates = byBarcode.get(key(r.barcode))!;
    else if (r.stockCode && byCode.has(key(r.stockCode))) candidates = byCode.get(key(r.stockCode))!;

    if (candidates.length > 1) {
      const lot = r.lot ? key(r.lot) : '';
      const byLot = lot ? candidates.filter((l) => key(l.lotNo) === lot) : [];
      if (byLot.length === 1) candidates = byLot;
      else {
        plan.invalid.push({ rowNo: r.rowNo, reason: 'Lotlu ürün: hangi lot olduğu "Lot" sütununda yazılmalı' });
        continue;
      }
    }
    if (candidates.length === 1) {
      const line = candidates[0];
      const m = acc.get(line.id) ?? { line, qty: 0, rows: [] };
      m.qty = Math.round((m.qty + r.qty) * 1000) / 1000;
      m.rows.push(r.rowNo);
      acc.set(line.id, m);
      continue;
    }
    if (r.barcode && r.qty > 0) plan.byBarcode.push(r);
    else plan.invalid.push({ rowNo: r.rowNo, reason: 'Sayım listesinde bulunamadı' });
  }
  plan.matched = [...acc.values()];
  return plan;
}
